import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ProfilePreferenceCode } from '@core/enums/profile-preference-code';
import { AgencyClientsRecord } from '@core/models/agency-clients-record';
import { BackendAPI } from '@core/models/backend-api';
import { CassError } from '@core/models/cass-error';
import { CustomerContact } from '@core/models/customer-contact.model';
import { Customer } from '@core/models/customer.model';
import { Preference } from '@core/models/preference';
import { UserProfile } from '@core/models/user-profile';
import { AuthService, AuthStatus } from '@core/services/auth.service';
import { CustomerService } from '@core/services/customer.service';
import { Logger } from '@core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { BehaviorSubject, Observable, distinctUntilChanged, filter, forkJoin, map, of, take, tap, throwError } from 'rxjs';
import { SubSink } from 'subsink';

@Injectable({
	providedIn: 'root'
})
export class UserProfilesService implements OnDestroy {

	private readonly COMPONENT_NAME = "UserProfilesService";

	readonly userProfile$ = new BehaviorSubject<UserProfile | undefined>(undefined);
	readonly userHasMultipleCustomers$ = new BehaviorSubject<boolean>(false);

	readonly callingOut$ = new BehaviorSubject<boolean>(false);
	private fullscreenProgressOverlayRef?: OverlayRef;

	private _agencyClientsRecords: AgencyClientsRecord[] | undefined;

	private _subs = new SubSink();

	constructor (
		private httpClient: HttpClient,
		private _authService: AuthService,
		private _logger: Logger,
		private _translate: TranslateService,
		private _customerService: CustomerService,
		private _overlay: Overlay) { }

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	init(): void {
		this.fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this._subs.add(
			this.callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					const componentRef = this.fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
					if (componentRef) {
						this._translate.get("APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE").subscribe((msg) => componentRef.instance.displayString = msg);
					}
				}
				else {
					this.fullscreenProgressOverlayRef?.detach();
				}
			}));

		this._subs.add(
			this.userProfile$.subscribe((userProfile: UserProfile | undefined) => {
				this.updateUserHasMultipleCustomers(userProfile);
			}));

		this._subs.add(
			this._authService.authStatus$.subscribe((authStatus: AuthStatus) => {
				this._agencyClientsRecords = undefined;
				if (authStatus.isAuthenticated) {
					this.callingOut$.next(true);
					this.loadUserProfile().subscribe({
						next: (userProfile: UserProfile) => {
							this.callingOut$.next(false);
							this.userProfile$.next(userProfile);
						},
						error: (error) => {
							this.callingOut$.next(false);
						}
					});
				}
				else {
					// notify the old profile is no longer valid
					this.userProfile$.next(undefined);
				}
			}));
	}

	getUserProfile(isReloadRequired: boolean = false): Observable<UserProfile | undefined> {
		if (this.userProfile$.value && !isReloadRequired) {
			return this.userProfile$;
		}
		return this.loadUserProfile();
	}

	updateUserProfile(profile: UserProfile): Observable<UserProfile> {
		const url = BackendAPI.userProfiles;

		return this.httpClient.patch<UserProfile>(url, profile);
	}

	private updateUserHasMultipleCustomers(userProfile: UserProfile | undefined): void {
		if (!userProfile) {
			// logout is processed here
			this.notifyUserHasMultipleCustomers(false);
			return;
		}

		if (!userProfile.customers || userProfile.customers.length === 0) {
			this._logger.error(this.COMPONENT_NAME, `The user profile "${userProfile.email}" has no customers.`);
			this.notifyUserHasMultipleCustomers(false);
			return;
		}

		if (userProfile.customers && userProfile.customers.length === 1 && !userProfile.customers[0].agency) {
			// CASS-299: the user profile has one customer and that customer is not an agency
			this.notifyUserHasMultipleCustomers(false);
			return;
		}

		this.notifyUserHasMultipleCustomers(true);
	}

	private notifyUserHasMultipleCustomers(userHasCustomers: boolean): void {
		if (this.userHasMultipleCustomers$.value !== userHasCustomers) {
			this.userHasMultipleCustomers$.next(userHasCustomers);
		}
	}

	getUserProfileClients(): Observable<AgencyClientsRecord[]> {
		if (this._agencyClientsRecords && this._agencyClientsRecords.length > 0) {
			return of(this._agencyClientsRecords);
		}

		const userProfile = this.userProfile$.value;

		if (!userProfile) {
			return throwError(() => CassError.createErrorWithText("The UserProfilesService instance is not initialized"));
		}

		if (!userProfile.customers || userProfile.customers.length === 0) {
			// system error: misconfiguration or bug. Check this condition in the init and here only log an error: shut the compiler up
			return throwError(() => CassError.createErrorWithText("The User Profile does not have any customers"));
		}

		const agenciesIds = userProfile.customers.map((customer: Customer) => customer.id) as string[];

		return forkJoin(
			agenciesIds.map((agencyId: string) => this._customerService.getCustomers(agencyId).pipe(
				map((clients: Customer[]) => {
					const agencyClientsRecord = new AgencyClientsRecord();
					agencyClientsRecord.agencyId = agencyId;
					agencyClientsRecord.clients = clients;
					return agencyClientsRecord;
				})
			))).pipe(
				tap((agencyClientsRecords: AgencyClientsRecord[]) => this._agencyClientsRecords = agencyClientsRecords)
			);
	}

	notifyCustomerAccessed(id: string): void {
		if (!id) {
			return;
		}

		// Note: take(1) ensures that the observable completes after emitting one value, so manual unsubscription is not required
		this.userProfile$.pipe(
			filter((userProfile: UserProfile | undefined) => Boolean(userProfile)),
			take(1)
		).subscribe({
			next: (userProfile: UserProfile | undefined) => {
				if (userProfile) {
					let pref = this.getProfilePreferenceByCode(userProfile, ProfilePreferenceCode.RecentCusomersListIds);
					if (!pref) {
						pref = Preference.create(ProfilePreferenceCode.RecentCusomersListIds);
					}

					// update profile's prefs, send it to BE, get partial profile from the BE, and apply updated prefs to the local profile
					const updatedPref = this.addOrUpdateLastAccessedCustomer(pref, id);
					this.updateBEandFEProfilePrefs(userProfile, updatedPref);
				}
				else {
					this._logger.error(this.COMPONENT_NAME, "notifyCustomerAccessed(): the userProfile is empty, possible bug.");
				}
			}
		});
	}

	private getProfilePreferenceByCode(userProfile: UserProfile, code: string): Preference | undefined {
		return userProfile.preferences?.find((pref: Preference) => pref.code === code);
	}

	private addOrUpdateLastAccessedCustomer(pref: Preference, customerId: string): Preference {
		let lastAccessedCustomers: string[] = pref.value ? JSON.parse(pref.value) : [];

		const idx = lastAccessedCustomers.indexOf(customerId);
		if (idx === 0) {
			// the last accessed advertiser id is at the beginning of the list, nothing to change
		}
		else {
			if (idx > 0) {
				// the id is in the midlle of the list, so delete it from the middle; it will be added to the beginning
				lastAccessedCustomers.splice(idx, 1);
			}
			lastAccessedCustomers.unshift(customerId); // add the id to the beginning of the array
			lastAccessedCustomers = lastAccessedCustomers.slice(0, 10); // keep only 10 items
			pref.value = JSON.stringify(lastAccessedCustomers);
		}

		return pref;
	}

	private updateBEandFEProfilePrefs(userProfile: UserProfile, pref: Preference): void {

		userProfile.preferences = [pref];

		const updateProfileSub = this.updateUserProfile(userProfile).subscribe({
			next: (updatedPartialUserProfile: UserProfile | undefined) => {
				if (updatedPartialUserProfile) {
					const updatedLocalUserProfile = Object.assign(new UserProfile(), userProfile, { preferences: updatedPartialUserProfile.preferences });
					this.userProfile$.next(updatedLocalUserProfile);
				}
				else {
					this._logger.error(this.COMPONENT_NAME, "updateBEandFEProfilePrefs(): the updatedPartialUserProfile is empty, possible bug.")
				}
			},
			complete: () => {
				updateProfileSub.unsubscribe();
			}
		})
	}

	private getPreferenceByCode(code: string): Preference | undefined {
		const userProfile = this.userProfile$.value;
		if (!userProfile) {
			return undefined;
		}

		return userProfile?.preferences?.find((pref: Preference) => pref.code === code);
	}

	getRecentCustomersIds(): string[] | undefined {
		const userProfile = this.userProfile$.value;
		if (!userProfile) {
			return undefined;
		}

		const pref = this.getPreferenceByCode(ProfilePreferenceCode.RecentCusomersListIds);
		if (!pref) {
			return undefined;
		}

		return pref.value && pref.value.length > 0 ? JSON.parse(pref.value) : undefined;
	}

	getAgencyContact(otherPayerId: string | undefined, agencyContactId: string | undefined): CustomerContact | undefined {
		const profile = this.userProfile$.value;
		if (!profile) {
			this._logger.error(this.COMPONENT_NAME, `getAgencyContact(${otherPayerId}, ${agencyContactId}) method is called when the profile is not set`);
			return undefined;
		}

		if (otherPayerId) {
			if (agencyContactId) {
				const customer = profile.customers?.find(x => x.id === otherPayerId);
				const contact = customer?.contacts?.find(x => x.id === agencyContactId);
				return contact;
			}
			else {
				return {
					firstName: profile?.firstName,
					lastName: profile?.lastName,
					email: profile?.email,
					phone: profile?.phone
				} as CustomerContact;
			}
		}
		else {
			return undefined;
		}
	}

	private loadUserProfile(): Observable<UserProfile> {
		const url = BackendAPI.userProfiles;

		// backend returns the user profile based on JWT added by the CassHttpInterceptorService for logged-in users
		return this.httpClient.get<UserProfile>(url).pipe(
			tap((userProfile: UserProfile) => {
				this.userProfile$.next(userProfile);
			})
		);
	}
}
